import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg, utm, CATEGORIES } from "../../lib/api";
import { track } from "../../lib/analytics";
import { useSite } from "../../context/SiteContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[0-9 ()-]{7,20}$/;

export const Field = ({ label, error, children, id, required }) => (
  <div>
    <label htmlFor={id} className="field-label">{label}{required && " *"}</label>
    {children}
    {error && <p className="mt-1 text-xs font-semibold text-black" role="alert" data-testid={`error-${id}`}>— {error}</p>}
  </div>
);

export function validate(f, required) {
  const e = {};
  required.forEach((k) => { if (!String(f[k] ?? "").trim()) e[k] = "Required"; });
  if (f.email && !EMAIL_RE.test(f.email)) e.email = "Enter a valid email";
  if (f.phone && !PHONE_RE.test(f.phone)) e.phone = "Enter a valid phone number";
  if (!f.consent) e.consent = "Please accept to continue";
  return e;
}

export const Consent = ({ f, set, errors, prefix }) => {
  const { site } = useSite();
  return (
    <div>
      <label className="flex gap-3 items-start text-xs text-[#333] cursor-pointer">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-black" checked={!!f.consent} onChange={(e) => set("consent", e.target.checked)} data-testid={`${prefix}-consent`} />
        <span>{site?.privacy_notice || "We use your details only to respond to your request."} <Link to="/privacy-policy" className="underline">Privacy Policy</Link></span>
      </label>
      {errors.consent && <p className="mt-1 text-xs font-semibold" role="alert">— {errors.consent}</p>}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" value={f.website || ""} onChange={(e) => set("website", e.target.value)} />
    </div>
  );
};

export const Success = ({ title, text, reference, testid }) => (
  <div className="border border-black p-8 bg-white" role="status" data-testid={testid}>
    <CheckCircle2 className="h-8 w-8" />
    <p className="font-display text-2xl font-bold mt-4">{title}</p>
    <p className="mt-2 text-sm text-[#333]">{text}</p>
    {reference && <p className="mt-4 font-mono text-sm">Reference: <strong data-testid={`${testid}-ref`}>{reference}</strong></p>}
  </div>
);

export function useForm(initial) {
  const [f, setF] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return { f, set, errors, setErrors, busy, setBusy, error, setError, done, setDone, setF };
}

export function EnquiryForm({ experience, defaultCategory = "", compact }) {
  const fm = useForm({ name: "", email: "", phone: "", category: experience?.category || defaultCategory, destination: experience?.destination?.name || "", preferred_date: "", participants: "", budget: "", message: "", consent: false });
  const { f, set, errors } = fm;
  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f, ["name", "email", "phone"]);
    fm.setErrors(e);
    if (Object.keys(e).length) return;
    fm.setBusy(true); fm.setError("");
    try {
      const r = await api.post("/public/enquiries", { ...f, participants: f.participants ? Number(f.participants) : null, experience_id: experience?.id || "", source_page: window.location.pathname, ...utm() });
      fm.setDone(r.data);
      track("generate_lead", { form: "general", category: f.category || "unspecified" }, `enq-${r.data.reference}`);
    } catch (err) { fm.setError(errMsg(err)); } finally { fm.setBusy(false); }
  };
  if (fm.done) return <Success title="Enquiry received" text={fm.done.message} reference={fm.done.reference} testid="enquiry-success" />;
  const inp = (k, props = {}) => <input id={`enq-${k}`} className="field" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`enquiry-${k}`} {...props} />;
  return (
    <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2" data-testid="enquiry-form">
      <Field label="Full name" id="enq-name" error={errors.name} required>{inp("name", { autoComplete: "name" })}</Field>
      <Field label="Email" id="enq-email" error={errors.email} required>{inp("email", { type: "email", autoComplete: "email" })}</Field>
      <Field label="Phone" id="enq-phone" error={errors.phone} required>{inp("phone", { type: "tel", autoComplete: "tel" })}</Field>
      <Field label="Interested service" id="enq-category">
        <select id="enq-category" className="field" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="enquiry-category">
          <option value="">Select a service</option>
          {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </Field>
      {!compact && <Field label="Preferred destination" id="enq-destination">{inp("destination")}</Field>}
      <Field label="Travel date" id="enq-preferred_date">{inp("preferred_date", { type: "date", min: new Date().toISOString().slice(0, 10) })}</Field>
      <Field label="Participants" id="enq-participants">{inp("participants", { type: "number", min: 1, max: 500 })}</Field>
      {!compact && <Field label="Budget (optional)" id="enq-budget">{inp("budget", { placeholder: "e.g. ₹3,000 per person" })}</Field>}
      <div className="sm:col-span-2"><Field label="Message" id="enq-message"><textarea id="enq-message" rows={4} className="field" value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="enquiry-message" /></Field></div>
      <div className="sm:col-span-2"><Consent f={f} set={set} errors={errors} prefix="enquiry" /></div>
      {fm.error && <p className="sm:col-span-2 border border-black p-3 text-sm" role="alert" data-testid="enquiry-error">{fm.error}</p>}
      <div className="sm:col-span-2"><button type="submit" className="btn-primary w-full sm:w-auto" disabled={fm.busy} data-testid="enquiry-submit">{fm.busy && <Loader2 className="h-4 w-4 animate-spin" />}Send Enquiry</button></div>
    </form>
  );
}

export function CorporateForm() {
  const fm = useForm({ company: "", name: "", email: "", phone: "", preferred_service: "Corporate Trek", destination: "", group_size: "", preferred_date: "", event_duration: "", budget: "", message: "", consent: false });
  const { f, set, errors } = fm;
  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f, ["company", "name", "email", "phone", "group_size"]);
    if (f.group_size && Number(f.group_size) < 2) e.group_size = "Group size must be at least 2";
    fm.setErrors(e);
    if (Object.keys(e).length) return;
    fm.setBusy(true); fm.setError("");
    try {
      const r = await api.post("/public/corporate-enquiries", { ...f, group_size: Number(f.group_size), source_page: window.location.pathname, ...utm() });
      fm.setDone(r.data);
      track("generate_lead", { form: "corporate" }, `corp-${r.data.reference}`);
    } catch (err) { fm.setError(errMsg(err)); } finally { fm.setBusy(false); }
  };
  if (fm.done) return <Success title="Proposal request received" text={fm.done.message} reference={fm.done.reference} testid="corporate-success" />;
  const inp = (k, props = {}) => <input id={`corp-${k}`} className="field" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`corporate-${k}`} {...props} />;
  return (
    <form onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2" data-testid="corporate-form">
      <Field label="Company name" id="corp-company" error={errors.company} required>{inp("company", { autoComplete: "organization" })}</Field>
      <Field label="Contact person" id="corp-name" error={errors.name} required>{inp("name")}</Field>
      <Field label="Business email" id="corp-email" error={errors.email} required>{inp("email", { type: "email" })}</Field>
      <Field label="Phone number" id="corp-phone" error={errors.phone} required>{inp("phone", { type: "tel" })}</Field>
      <Field label="Preferred service" id="corp-preferred_service">
        <select id="corp-preferred_service" className="field" value={f.preferred_service} onChange={(e) => set("preferred_service", e.target.value)} data-testid="corporate-preferred_service">
          {CATEGORIES.map((c) => <option key={c.key}>{c.label}</option>)}
        </select>
      </Field>
      <Field label="Preferred destination" id="corp-destination">{inp("destination")}</Field>
      <Field label="Expected group size" id="corp-group_size" error={errors.group_size} required>{inp("group_size", { type: "number", min: 2 })}</Field>
      <Field label="Preferred date" id="corp-preferred_date">{inp("preferred_date", { type: "date", min: new Date().toISOString().slice(0, 10) })}</Field>
      <Field label="Event duration" id="corp-event_duration">{inp("event_duration", { placeholder: "e.g. 1 day, 2 days / 1 night" })}</Field>
      <Field label="Budget range (optional)" id="corp-budget">{inp("budget")}</Field>
      <div className="sm:col-span-2"><Field label="Requirements & message" id="corp-message"><textarea id="corp-message" rows={4} className="field" value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="corporate-message" /></Field></div>
      <div className="sm:col-span-2"><Consent f={f} set={set} errors={errors} prefix="corporate" /></div>
      {fm.error && <p className="sm:col-span-2 border border-black p-3 text-sm" role="alert" data-testid="corporate-error">{fm.error}</p>}
      <div className="sm:col-span-2"><button type="submit" className="btn-primary" disabled={fm.busy} data-testid="corporate-submit">{fm.busy && <Loader2 className="h-4 w-4 animate-spin" />}Request a Corporate Proposal</button></div>
    </form>
  );
}
